import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import StructuredData from '@/components/base/StructuredData';

const milestones = [
  {
    year: '2019',
    title: 'Founded',
    desc: '4K Dream was founded with a simple mission: make premium TV accessible to everyone, anywhere in the world, at a fair price.',
  },
  {
    year: '2020',
    title: 'First 1,000 Subscribers',
    desc: 'Word spread fast. Within our first year we reached our first thousand subscribers across Europe and North America.',
  },
  {
    year: '2021',
    title: 'Anti-Freeze Technology',
    desc: 'We invested in our own server infrastructure and developed our proprietary Anti-Freeze streaming technology for uninterrupted viewing.',
  },
  {
    year: '2022',
    title: 'International Expansion',
    desc: 'Coverage expanded to 150+ countries. We added dedicated Arabic, African, and Asian channel packages to serve a global audience.',
  },
  {
    year: '2023',
    title: '4K Ultra HD Launch',
    desc: 'We upgraded our entire catalogue to support 4K Ultra HD streaming, delivering cinema-quality picture on any compatible device.',
  },
  {
    year: '2024',
    title: 'Home Visit Support',
    desc: 'We launched our unique Home Visit support service — our technicians come to you for in-person setup and troubleshooting.',
  },
  {
    year: '2025',
    title: 'Replay & EPG Features',
    desc: 'Launched 24h Replay and a full Electronic Programme Guide (EPG) for Gold subscribers, making it easier than ever to never miss a show.',
  },
  {
    year: '2026',
    title: 'Today',
    desc: 'Thousands of satisfied customers across 150+ countries trust 4K Dream as their go-to premium streaming service.',
  },
];

const values = [
  {
    icon: 'ri-shield-check-line',
    gradient: 'from-emerald-500 to-teal-500',
    title: 'Transparency',
    desc: 'No hidden fees, no fake discounts, no misleading claims. What you see is exactly what you get — honest pricing, honest service.',
  },
  {
    icon: 'ri-customer-service-2-line',
    gradient: 'from-[#862ce0] to-[#c026d3]',
    title: 'Customer First',
    desc: 'Our 7-day money-back guarantee and 7-days-a-week support team exist because we genuinely believe in putting customers before profit.',
  },
  {
    icon: 'ri-global-line',
    gradient: 'from-cyan-500 to-blue-500',
    title: 'Accessibility',
    desc: 'Premium TV shouldn\'t be a luxury. We work hard to keep our prices competitive so families everywhere can enjoy quality entertainment.',
  },
  {
    icon: 'ri-flashlight-line',
    gradient: 'from-amber-400 to-orange-500',
    title: 'Reliability',
    desc: 'Our Anti-Freeze technology and 99.9% uptime commitment mean you can count on us — whether it\'s a Champions League final or a family movie night.',
  },
];

const team = [
  {
    name: 'Alex M.',
    role: 'Founder & CEO',
    desc: 'Streaming technology enthusiast with 10+ years in digital media. Founded 4K Dream to bring affordable premium TV to the world.',
    img: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20confident%20male%20entrepreneur%20in%20his%2030s%2C%20wearing%20a%20dark%20blazer%2C%20clean%20studio%20background%2C%20soft%20lighting%2C%20sharp%20focus%2C%20modern%20corporate%20portrait%20photography&width=400&height=400&seq=team1&orientation=squarish',
    icon: 'ri-user-star-line',
  },
  {
    name: 'Sarah K.',
    role: 'Head of Customer Support',
    desc: 'Leads our 7-day support team with a passion for resolving issues fast. Personally oversees every escalated case.',
    img: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20friendly%20female%20customer%20service%20manager%20in%20her%20late%2020s%2C%20wearing%20a%20smart%20casual%20top%2C%20neutral%20studio%20background%2C%20warm%20smile%2C%20corporate%20portrait%20photography&width=400&height=400&seq=team2&orientation=squarish',
    icon: 'ri-customer-service-2-line',
  },
  {
    name: 'Marc D.',
    role: 'Head of Technical Infrastructure',
    desc: 'The engineer behind our Anti-Freeze technology and server architecture. Ensures 99.9% uptime across all regions.',
    img: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20male%20software%20engineer%20in%20his%2030s%2C%20wearing%20a%20casual%20shirt%2C%20modern%20office%20background%20with%20bokeh%2C%20confident%20expression%2C%20corporate%20portrait%20photography&width=400&height=400&seq=team3&orientation=squarish',
    icon: 'ri-server-line',
  },
  {
    name: 'Leila B.',
    role: 'Content & Partnerships Manager',
    desc: 'Manages our channel catalogue and content partnerships. Responsible for adding new channels and keeping the library fresh.',
    img: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20female%20content%20manager%20in%20her%20late%2020s%2C%20wearing%20a%20stylish%20blazer%2C%20bright%20neutral%20background%2C%20professional%20smile%2C%20modern%20corporate%20portrait%20photography&width=400&height=400&seq=team4&orientation=squarish',
    icon: 'ri-film-line',
  },
];

const stats = [
  { value: '7', label: 'Years in Business', icon: 'ri-calendar-line' },
  { value: '150+', label: 'Countries Covered', icon: 'ri-global-line' },
  { value: '25,000+', label: 'Live Channels', icon: 'ri-tv-line' },
  { value: '70,000+', label: 'Movies & Series', icon: 'ri-movie-2-line' },
];

const aboutSchemas = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About 4K Dream',
    url: 'https://www.4k-dream.com/about',
    description: '4K Dream is an independent premium streaming service founded in 2019. Learn about our story, team, and values.',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.4k-dream.com/' },
        { '@type': 'ListItem', position: 2, name: 'About Us', item: 'https://www.4k-dream.com/about' },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '4K Dream',
    url: 'https://www.4k-dream.com',
    logo: 'https://static.readdy.ai/image/5c7545dbb6198b28906c423b0244c4c9/0789dc6d730dd15b6f1e8e24be7c3580.webp',
    foundingDate: '2019',
    numberOfEmployees: { '@type': 'QuantitativeValue', value: 10 },
    description: 'Independent premium streaming service providing live TV channels, movies, and series in HD/4K to 150+ countries since 2019.',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+1-205-709-2376',
      email: 'support@4k-dream.com',
      contactType: 'customer support',
    },
  },
];

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <StructuredData schemas={aboutSchemas} id="about" />
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              alt="4K Dream Logo"
              className="h-9 w-auto object-contain"
              src="https://static.readdy.ai/image/5c7545dbb6198b28906c423b0244c4c9/0789dc6d730dd15b6f1e8e24be7c3580.webp"
            />
          </Link>
          <nav className="hidden sm:flex items-center gap-6 text-sm text-white/60">
            <a href="#story" className="hover:text-white transition-colors cursor-pointer">Our Story</a>
            <a href="#values" className="hover:text-white transition-colors cursor-pointer">Values</a>
            <a href="#team" className="hover:text-white transition-colors cursor-pointer">Team</a>
            <a href="#contact" className="hover:text-white transition-colors cursor-pointer">Contact</a>
          </nav>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="relative bg-gradient-to-br from-[#1a0a2e] via-[#0d0d1a] to-[#0a0a0f] py-20 sm:py-28 border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#862ce0]/10 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#c026d3]/8 rounded-full blur-[100px]"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-[#862ce0]/20 border border-[#862ce0]/30 rounded-full px-4 py-2 mb-6">
            <i className="ri-building-line text-[#b47ef3] text-sm"></i>
            <span className="text-[#b47ef3] text-sm font-medium">Est. 2019 · Independent Streaming Service</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black mb-6 leading-tight">
            About{' '}
            <span className="bg-gradient-to-r from-[#862ce0] to-[#c026d3] bg-clip-text text-transparent">
              4K Dream
            </span>
          </h1>
          <p className="text-white/70 text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed">
            We are an independent premium streaming service founded in 2019. For over 7 years, we have been delivering high-quality live TV and on-demand content to thousands of customers across 150+ countries.
          </p>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-gray-900/80 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 rounded-xl bg-[#862ce0]/20">
                  <i className={`${s.icon} text-[#b47ef3] text-lg`}></i>
                </div>
                <div className="text-2xl sm:text-3xl font-black text-white mb-1">{s.value}</div>
                <div className="text-xs sm:text-sm text-gray-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20 space-y-20">

        {/* Our Story */}
        <section id="story">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#862ce0]/10 border border-[#862ce0]/20 rounded-full px-4 py-1.5 mb-5">
                <i className="ri-history-line text-[#b47ef3] text-sm"></i>
                <span className="text-[#b47ef3] text-sm font-medium">Our Story</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-5 leading-tight">
                7 Years of Bringing the World to Your Screen
              </h2>
              <div className="space-y-4 text-white/70 leading-relaxed">
                <p>
                  4K Dream was born in 2019 out of a simple frustration: premium TV was too expensive, too complicated, and too fragmented. Families were paying for multiple subscriptions just to watch what they loved.
                </p>
                <p>
                  We set out to build something different — a single, affordable service that brings together live channels, sports, movies, and series from around the world, all in one place, with no contracts and no hidden fees.
                </p>
                <p>
                  Today, thousands of customers across Europe, North America, Africa, and beyond trust 4K Dream as their primary entertainment platform. We are proud to be an independent service — not affiliated with any broadcaster or platform — built entirely on our own infrastructure and our own team.
                </p>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a
                  href="https://wa.me/12057092376"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#862ce0] to-[#c026d3] text-white font-semibold px-6 py-3 rounded-xl transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line"></i>
                  Talk to Us
                </a>
                <Link
                  to="/#pricing"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/80 hover:text-white hover:border-white/40 font-semibold px-6 py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap"
                >
                  View Plans
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            {/* Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#862ce0]/20 to-[#c026d3]/20 rounded-3xl blur-2xl"></div>
              <div className="relative rounded-3xl overflow-hidden aspect-[4/3]">
                <img
                  alt="4K Dream team working on streaming infrastructure"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                  src="https://readdy.ai/api/search-image?query=modern%20tech%20startup%20office%20with%20diverse%20team%20members%20working%20on%20multiple%20screens%20showing%20streaming%20dashboards%20and%20analytics%2C%20warm%20ambient%20lighting%2C%20open%20plan%20workspace%20with%20dark%20aesthetic%2C%20professional%20photography%2C%20cinematic%20wide%20angle%20shot&width=800&height=600&seq=about-office&orientation=landscape"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="inline-flex items-center gap-2 bg-black/60 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                    <span className="text-white text-xs font-medium">Independent service since 2019</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section>
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-time-line text-amber-400 text-sm"></i>
              <span className="text-amber-400 text-sm font-medium">Our Journey</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-white">From Startup to Trusted Service</h2>
          </div>

          <div className="relative">
            {/* Center line desktop */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#862ce0]/50 via-[#862ce0]/20 to-transparent -translate-x-1/2"></div>

            <div className="space-y-6 lg:space-y-0">
              {milestones.map((m, i) => (
                <div
                  key={m.year}
                  className={`relative lg:flex lg:items-center lg:gap-8 lg:mb-8 ${
                    i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`}
                >
                  {/* Card */}
                  <div className="lg:w-[calc(50%-2rem)] bg-white/5 border border-white/10 rounded-2xl p-5 sm:p-6 hover:border-[#862ce0]/40 transition-all duration-300 mb-4 lg:mb-0">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#862ce0]/30 to-[#c026d3]/20 border border-[#862ce0]/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-[#b47ef3] font-black text-sm">{m.year}</span>
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-base sm:text-lg mb-1">{m.title}</h3>
                        <p className="text-white/60 text-sm leading-relaxed">{m.desc}</p>
                      </div>
                    </div>
                  </div>

                  {/* Center dot */}
                  <div className="hidden lg:flex w-4 h-4 rounded-full bg-[#862ce0] border-2 border-[#c026d3] flex-shrink-0 z-10 mx-auto"></div>

                  {/* Spacer */}
                  <div className="hidden lg:block lg:w-[calc(50%-2rem)]"></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section id="values">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-heart-line text-emerald-400 text-sm"></i>
              <span className="text-emerald-400 text-sm font-medium">What We Stand For</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">Our Core Values</h2>
            <p className="text-white/60 max-w-xl mx-auto">These aren't just words on a page — they're the principles that guide every decision we make.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {values.map((v) => (
              <div
                key={v.title}
                className="group bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#862ce0]/40 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${v.gradient} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${v.icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-lg mb-2 group-hover:text-[#b47ef3] transition-colors">{v.title}</h3>
                    <p className="text-white/60 text-sm leading-relaxed">{v.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Team */}
        <section id="team">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#862ce0]/10 border border-[#862ce0]/20 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-team-line text-[#b47ef3] text-sm"></i>
              <span className="text-[#b47ef3] text-sm font-medium">The People Behind 4K Dream</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">Meet Our Team</h2>
            <p className="text-white/60 max-w-xl mx-auto">A small, dedicated team passionate about streaming technology and customer satisfaction.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {team.map((member) => (
              <div
                key={member.name}
                className="group bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-[#862ce0]/40 transition-all duration-300"
              >
                <div className="aspect-square overflow-hidden relative">
                  <img
                    alt={`${member.name} - ${member.role}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    src={member.img}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                  <div className="absolute bottom-3 left-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#862ce0]/80 backdrop-blur-sm">
                      <i className={`${member.icon} text-white text-sm`}></i>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-white text-base mb-0.5">{member.name}</h3>
                  <p className="text-[#b47ef3] text-xs font-semibold mb-2">{member.role}</p>
                  <p className="text-white/50 text-xs leading-relaxed">{member.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Disclaimer */}
        <section>
          <div className="bg-gray-800/50 border border-white/10 rounded-2xl p-6 flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-500/20 flex-shrink-0">
              <i className="ri-information-line text-amber-400 text-lg"></i>
            </div>
            <div>
              <h3 className="font-bold text-white mb-2">Independent Service Disclaimer</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                4K Dream is an <strong className="text-white">independent streaming service</strong> and is not affiliated with, endorsed by, or in any way associated with Netflix, Disney+, Amazon Prime Video, Canal+, beIN Sports, or any other third-party platform or broadcaster. All brand names and trademarks mentioned on this site are the property of their respective owners. 4K Dream provides access to its own independently licensed content catalogue.
              </p>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section id="contact">
          <div className="bg-gradient-to-br from-[#862ce0]/20 to-[#c026d3]/10 border border-[#862ce0]/30 rounded-2xl p-8 sm:p-10 text-center">
            <h2 className="text-2xl sm:text-3xl font-black text-white mb-3">Get in Touch</h2>
            <p className="text-white/70 mb-8 max-w-lg mx-auto text-sm sm:text-base">
              Have a question about our service, a partnership enquiry, or just want to say hello? Our team is available 7 days a week from 10am to midnight.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a
                href="https://wa.me/12057092376"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold px-8 py-4 rounded-xl transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                WhatsApp
              </a>
              <a
                href="mailto:support@4k-dream.com"
                className="inline-flex items-center justify-center gap-2 border border-[#862ce0]/50 text-[#b47ef3] hover:bg-[#862ce0]/20 font-semibold px-8 py-4 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-line text-xl"></i>
                support@4k-dream.com
              </a>
              <a
                href="tel:+12057092376"
                className="inline-flex items-center justify-center gap-2 border border-white/15 text-white/70 hover:text-white hover:border-white/30 font-semibold px-8 py-4 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                +1 (205) 709 2376
              </a>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-white/50 text-sm">
              <span className="flex items-center gap-2"><i className="ri-time-line text-[#b47ef3]"></i> 7 days/7, 10am–midnight</span>
              <span className="flex items-center gap-2"><i className="ri-map-pin-line text-[#b47ef3]"></i> Serving 150+ countries</span>
              <span className="flex items-center gap-2"><i className="ri-shield-check-line text-[#b47ef3]"></i> Est. 2019</span>
            </div>
          </div>
        </section>

        {/* Bottom nav */}
        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors cursor-pointer text-sm">
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
          <div className="flex items-center gap-6 flex-wrap justify-center">
            <Link to="/privacy" className="text-white/50 hover:text-[#b47ef3] transition-colors text-sm cursor-pointer">Privacy Policy</Link>
            <Link to="/terms" className="text-white/50 hover:text-[#b47ef3] transition-colors text-sm cursor-pointer">Terms &amp; Conditions</Link>
            <Link to="/refund" className="text-white/50 hover:text-emerald-400 transition-colors text-sm cursor-pointer">Refund Policy</Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black/50 border-t border-white/10 py-6 px-4 text-center text-white/40 text-sm">
        <p>
          4K Dream 2019-2026 © All Rights Reserved &nbsp;·&nbsp;
          <Link to="/privacy" className="hover:text-[#b47ef3] transition-colors">Privacy Policy</Link>
          &nbsp;·&nbsp;
          <Link to="/terms" className="hover:text-[#b47ef3] transition-colors">Terms &amp; Conditions</Link>
          &nbsp;·&nbsp;
          <Link to="/refund" className="hover:text-emerald-400 transition-colors">Refund Policy</Link>
        </p>
      </footer>
    </div>
  );
};

export default AboutPage;
